import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Box,
  Button,
  Chip,
  Drawer,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import SellIcon from '@mui/icons-material/Sell';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn';
import { useTheme } from '@mui/material/styles';
import SearchBar from './SearchBar';
import FilterSelector from './FilterSelector';
import ViewModeSelector from './ViewModeSelector';

/**
 * Reusable collection header component with search, filter, and view mode controls
 * @param {Object} props - Component props
 * @param {string} props.title - Collection title
 * @param {string} props.searchQuery - Current search query
 * @param {function} props.handleSearchChange - Function to handle search input changes
 * @param {function} props.handleSearchKeyPress - Function to handle key press events
 * @param {function} props.handleSearch - Function to handle search submission
 * @param {string} props.filterValue - Current filter value
 * @param {function} props.handleFilterChange - Function to handle filter changes
 * @param {Array} props.filterOptions - Array of filter options
 * @param {string} props.filterLabel - Label for the filter selector
 * @param {function} props.handleClearFilters - Function to clear all filters
 * @param {string} props.viewMode - Current view mode
 * @param {function} props.handleViewModeChange - Function to handle view mode changes
 * @returns {JSX.Element} Collection header component
 */
const CollectionHeader = ({
  title,
  searchQuery,
  handleSearchChange,
  handleSearchKeyPress,
  handleSearch,
  onScanBarcode,
  filterValue,
  handleFilterChange,
  filterOptions,
  filterLabel = "Filter by Platform",
  handleClearFilters,
  viewMode,
  handleViewModeChange,
  isWishlist = false,
  hideShelf: hideShelfProp = false,
  sortValue,
  handleSortChange,
  sortOptions = [
    { value: 'alpha_asc', label: 'Alphabetical (A→Z)' },
    { value: 'alpha_desc', label: 'Alphabetical (Z→A)' },
    { value: 'date_desc', label: 'Date Added (Newest)' },
    { value: 'date_asc', label: 'Date Added (Oldest)' },
    { value: 'year_desc', label: 'Release Year (Newest)' },
    { value: 'year_asc', label: 'Release Year (Oldest)' },
    { value: 'rating_desc', label: 'Rating (High→Low)' },
    { value: 'rating_asc', label: 'Rating (Low→High)' },
    { value: 'favourites_first', label: 'Favourites First' }
  ],
  statusFilterValue,
  handleStatusFilterChange,
  statusFilterOptions = [],
  showFavouritesOnly = false,
  onToggleFavouritesFilter,
  showSoldOnly = false,
  onToggleSoldFilter,
  showBacklogOnly = false,
  onToggleBacklogFilter,
  showDNFOnly = false,
  onToggleDNFFilter
}) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <Box sx={{ my: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {title}
      </Typography>

      {/* Combined Search and Filter Controls */}
      <Grid container spacing={2} sx={{ mb: 4, alignItems: 'stretch' }}>
        <Grid item xs={12} md={5}>
          <SearchBar
            searchQuery={searchQuery}
            handleSearchChange={handleSearchChange}
            handleSearchKeyPress={handleSearchKeyPress}
            handleSearch={handleSearch}
            onScanBarcode={onScanBarcode}
            placeholder={`Search your ${title ? title.toLowerCase() : 'collection'} or find new items...`}
            sx={{ mb: { xs: 1, sm: 0 } }}
          />
        </Grid>
        {/* Desktop controls */}
        {!isSmall && (
          <Grid item xs={12} md={7}>
            {/* Row 1: dropdowns + view mode */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch', flexWrap: 'nowrap' }}>
              <Box sx={{ flex: '1 1 0', minWidth: 120 }}>
                <FilterSelector
                  value={filterValue}
                  onChange={handleFilterChange}
                  label={filterLabel}
                  options={filterOptions}
                  defaultOption={`All ${filterLabel.split(' ').pop()}`}
                />
              </Box>
              {statusFilterOptions.length > 0 && (
                <Box sx={{ flex: '1 1 0', minWidth: 100 }}>
                  <FormControl fullWidth>
                    <InputLabel id="status-filter-label">Status</InputLabel>
                    <Select
                      labelId="status-filter-label"
                      id="status-filter"
                      label="Status"
                      value={statusFilterValue || ''}
                      onChange={(e) => handleStatusFilterChange && handleStatusFilterChange(e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      {statusFilterOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
              <Box sx={{ flex: '1 1 0', minWidth: 100 }}>
                <FormControl fullWidth>
                  <InputLabel id="sort-by-label">Sort By</InputLabel>
                  <Select
                    labelId="sort-by-label"
                    id="sort-by"
                    label="Sort By"
                    value={sortValue || 'alpha_asc'}
                    onChange={(e) => handleSortChange && handleSortChange(e.target.value)}
                  >
                    {sortOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <ViewModeSelector
                  viewMode={viewMode}
                  handleViewModeChange={handleViewModeChange}
                  hideShelf={isWishlist || hideShelfProp}
                />
              </Box>
              {/* Clear button for wishlists — Row 2 chips are hidden for wishlists so we
                  need an alternative way to reset an active platform/format filter */}
              {isWishlist && filterValue && handleClearFilters && (
                <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'stretch' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleClearFilters}
                    sx={{ whiteSpace: 'nowrap', height: '100%' }}
                  >
                    Clear
                  </Button>
                </Box>
              )}
            </Box>

            {/* Row 2: filter chips + clear (only rendered when at least one chip is available) */}
            {(!isWishlist && (onToggleFavouritesFilter || onToggleSoldFilter || onToggleBacklogFilter || onToggleDNFFilter)) && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
                {onToggleFavouritesFilter && (
                  <Chip
                    icon={<FavoriteIcon />}
                    label="Favourites"
                    clickable
                    onClick={onToggleFavouritesFilter}
                    color={showFavouritesOnly ? 'error' : 'default'}
                    variant={showFavouritesOnly ? 'filled' : 'outlined'}
                    size="small"
                  />
                )}
                {onToggleSoldFilter && (
                  <Chip
                    icon={<SellIcon />}
                    label="Sold"
                    clickable
                    onClick={onToggleSoldFilter}
                    color={showSoldOnly ? 'warning' : 'default'}
                    variant={showSoldOnly ? 'filled' : 'outlined'}
                    size="small"
                  />
                )}
                {onToggleBacklogFilter && (
                  <Chip
                    icon={<BookmarkIcon />}
                    label="Backlog"
                    clickable
                    onClick={onToggleBacklogFilter}
                    color={showBacklogOnly ? 'secondary' : 'default'}
                    variant={showBacklogOnly ? 'filled' : 'outlined'}
                    size="small"
                  />
                )}
                {onToggleDNFFilter && (
                  <Chip
                    icon={<DoNotDisturbOnIcon />}
                    label="DNF"
                    clickable
                    onClick={onToggleDNFFilter}
                    color={showDNFOnly ? 'warning' : 'default'}
                    variant={showDNFOnly ? 'filled' : 'outlined'}
                    size="small"
                  />
                )}
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleClearFilters}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Clear
                </Button>
              </Box>
            )}
          </Grid>
        )}

        {/* Mobile filter button */}
        {isSmall && (
          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setDrawerOpen(true)}
              sx={{ height: '56px' }}
            >
              Filters & Sort
            </Button>
          </Grid>
        )}
      </Grid>

      {/* Mobile Drawer for Filters/Sort */}
      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FilterSelector
                value={filterValue}
                onChange={handleFilterChange}
                label={filterLabel}
                options={filterOptions}
                defaultOption={`All ${filterLabel.split(' ').pop()}`}
              />
            </Grid>
            {statusFilterOptions.length > 0 && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="status-filter-label-mobile">Status</InputLabel>
                  <Select
                    labelId="status-filter-label-mobile"
                    id="status-filter-mobile"
                    label="Status"
                    value={statusFilterValue || ''}
                    onChange={(e) => handleStatusFilterChange && handleStatusFilterChange(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {statusFilterOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="sort-by-label-mobile">Sort By</InputLabel>
                <Select
                  labelId="sort-by-label-mobile"
                  id="sort-by-mobile"
                  label="Sort By"
                  value={sortValue || 'alpha_asc'}
                  onChange={(e) => handleSortChange && handleSortChange(e.target.value)}
                >
                  {sortOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <ViewModeSelector
                viewMode={viewMode}
                handleViewModeChange={handleViewModeChange}
                hideShelf={isWishlist}
              />
            </Grid>
            {!isWishlist && onToggleFavouritesFilter && (
              <Grid item xs={12}>
                <Chip
                  icon={<FavoriteIcon />}
                  label="Favourites Only"
                  clickable
                  onClick={() => { onToggleFavouritesFilter(); setDrawerOpen(false); }}
                  color={showFavouritesOnly ? 'error' : 'default'}
                  variant={showFavouritesOnly ? 'filled' : 'outlined'}
                  sx={{ width: '100%', height: '40px', borderRadius: 1 }}
                />
              </Grid>
            )}
            {!isWishlist && onToggleSoldFilter && (
              <Grid item xs={12}>
                <Chip
                  icon={<SellIcon />}
                  label="Sold Items Only"
                  clickable
                  onClick={() => { onToggleSoldFilter(); setDrawerOpen(false); }}
                  color={showSoldOnly ? 'warning' : 'default'}
                  variant={showSoldOnly ? 'filled' : 'outlined'}
                  sx={{ width: '100%', height: '40px', borderRadius: 1 }}
                />
              </Grid>
            )}
            {!isWishlist && onToggleBacklogFilter && (
              <Grid item xs={12}>
                <Chip
                  icon={<BookmarkIcon />}
                  label="Backlog Only"
                  clickable
                  onClick={() => { onToggleBacklogFilter(); setDrawerOpen(false); }}
                  color={showBacklogOnly ? 'secondary' : 'default'}
                  variant={showBacklogOnly ? 'filled' : 'outlined'}
                  sx={{ width: '100%', height: '40px', borderRadius: 1 }}
                />
              </Grid>
            )}
            {!isWishlist && onToggleDNFFilter && (
              <Grid item xs={12}>
                <Chip
                  icon={<DoNotDisturbOnIcon />}
                  label="DNF Only"
                  clickable
                  onClick={() => { onToggleDNFFilter(); setDrawerOpen(false); }}
                  color={showDNFOnly ? 'warning' : 'default'}
                  variant={showDNFOnly ? 'filled' : 'outlined'}
                  sx={{ width: '100%', height: '40px', borderRadius: 1 }}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Button fullWidth variant="outlined" onClick={() => { handleClearFilters && handleClearFilters(); setDrawerOpen(false); }}>
                Clear Filters
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button fullWidth variant="contained" onClick={() => setDrawerOpen(false)}>
                Done
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Drawer>
    </Box>
  );
};

export default CollectionHeader;
